// HM-322 cluster 15.5: pagina /camere SPEC mockup v3.
// HM-323: aggiunto RoomFilterBar mono-selezione + zustand store.
// Layout: titolo + filter bar (HM-323) + RoomGrid + click tile ->
// drawer dettaglio (placeholder, vero in HM-324).

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { fetchBookingsWindow, fetchRooms } from '../api/rooms.js';
import { CheckoutModal } from '../components/checkout/CheckoutModal.js';
import { RoomGrid } from '../components/dashboard-v2/RoomGrid.js';
import { RoomDetailDrawer } from '../components/rooms/RoomDetailDrawer.js';
import { RoomFilterBar } from '../components/rooms/RoomFilterBar.js';
import { WalkInModal } from '../components/walk-in/WalkInModal.js';
import { useDashboardLiveSync } from '../hooks/useDashboardLiveSync.js';
import { useWsConnection } from '../hooks/useWsEvents.js';
import { useRoomFilterStore } from '../stores/roomFilter.js';

export function RoomsPage() {
  const ws = useWsConnection();
  // useDashboardLiveSync side-effect: invalidates rooms query on WS event
  // -> mantiene la grid live anche in /camere (riusa la stessa cache).
  // HM-473 audit A7: blinkingExitRoomIds = camere col codice uscita appena
  // usato (Spec §7.4), passato a RoomGrid per accendere il blink.
  const liveSync = useDashboardLiveSync(ws);

  const now = useNowTick(60_000);
  // HM-530 fix camere: la query bookings deve coprire ANCHE bookings
  // `checked_in` che hanno end_at gia' nel passato (overstay) altrimenti
  // il drawer non trova l'activeBooking e il bottone Check-out resta
  // disabilitato. Finestra estesa: ±30g intorno a now.
  const windowStart = useMemo(() => {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d;
  }, [now]);
  const windowEnd = useMemo(() => {
    const d = new Date(now);
    d.setDate(d.getDate() + 30);
    return d;
  }, [now]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const filter = useRoomFilterStore((s) => s.selectedFilter);

  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms });
  const bookingsQuery = useQuery({
    queryKey: ['bookings', { window: '60d' }],
    queryFn: () =>
      fetchBookingsWindow({ from: windowStart.toISOString(), to: windowEnd.toISOString() }),
    staleTime: 30_000,
  });

  return (
    <div
      className="px-6 py-6 h-full flex flex-col min-h-0 max-xl:px-3 max-xl:py-3"
      data-testid="rooms-page"
    >
      {/* HM-600 iPad UX: header + filtri fissi (shrink-0), scorre solo la griglia. */}
      <div className="shrink-0">
        <h1 className="text-2xl max-xl:text-xl font-bold text-text-primary tracking-[-0.02em] mb-4 max-xl:mb-2.5">
          Camere
        </h1>

        {roomsQuery.data && <RoomFilterBar rooms={roomsQuery.data.rooms} />}

        {roomsQuery.isLoading && <p className="text-text-secondary">Caricamento camere…</p>}
        {roomsQuery.error && (
          <div className="rounded-md p-3 text-sm bg-state-guas/10 text-state-guas border border-state-guas/30">
            Errore caricamento camere: {(roomsQuery.error as Error).message}
          </div>
        )}
      </div>

      {roomsQuery.data && (
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="rooms-scroll">
          <RoomGrid
            rooms={roomsQuery.data.rooms}
            bookings={bookingsQuery.data?.bookings ?? []}
            now={now}
            selectedRoomId={selectedRoomId}
            onSelect={(room) => setSelectedRoomId(room.id)}
            filter={filter}
            blinkingExitRoomIds={liveSync.blinkingExitRoomIds}
          />
        </div>
      )}

      <RoomDetailDrawer
        room={
          selectedRoomId
            ? (roomsQuery.data?.rooms.find((r) => r.id === selectedRoomId) ?? null)
            : null
        }
        bookings={bookingsQuery.data?.bookings ?? []}
        now={now}
        isOpen={selectedRoomId !== null}
        onClose={() => setSelectedRoomId(null)}
      />

      {/* Modali condivisi triggerati dal drawer per check-in/out walk-in. */}
      <WalkInModal />
      <CheckoutModal />
    </div>
  );
}

function useNowTick(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
